import { Card, InputGroup, Menu, MenuItem, popoverPropsToNextProps } from "@blueprintjs/core";
import { PlanrPopover } from "modules/common/components/planr/popover/Popover";
import classnames from "classnames";
import { FieldProps } from "formik";
import { observer } from "mobx-react";
import { FileLink } from "modules/common/components/files/FileLink";
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation";
import { LocalLoader } from "modules/common/components/loader/Loader";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { PlanRDndUploader } from "modules/common/components/planr/dndUploader/FileDndUploader";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { matchTargetWidthPopover } from "modules/common/services/form/select";
import { texts } from "modules/common/texts";
import React, { useRef, useState } from "react";
import { v4 } from "uuid";
import { ACCEPT, EmployerDocumentBlockForm, EmployerFileBase, EmployerType } from "../../models/employer";
import styles from "./EmployerDocuments.module.scss";

// в placeholder переносов не бывает, поэтому короткий текст в поле, полный — подсказкой
const NOTE_PLACEHOLDER = "Примечание или выбор из списка";
const NOTE_HINT = "Введите примечание к файлу или выберите из списка";

/**
 * Выпадающий список ровно по ширине поля. Пропсы у matchTargetWidthPopover старого
 * формата (position, minimal), PopoverNext их не понимает — переводим
 */
const MATCH_TARGET_WIDTH = popoverPropsToNextProps(matchTargetWidthPopover());

const Documents = ({ baseUrl, fieldProps, employer, canChange, notes }: EmployerDocumentsProps) => {
    const { field, form } = fieldProps;
    const blocks: EmployerDocumentBlockForm[] = field.value || [];

    const [selected, setSelected] = useState<TStringMap<boolean>>({});
    const [printing, setPrinting] = useState(false);
    // только что добавленный блок: подсвечиваем и ставим в него курсор
    const [freshGuid, setFreshGuid] = useState("");

    // в один блок можно бросить сразу несколько файлов, и загрузчик ждёт каждый по очереди:
    // без актуального значения под рукой второй файл затёр бы первый
    const valueRef = useRef(blocks);
    valueRef.current = blocks;

    const commit = (next: EmployerDocumentBlockForm[]) => {
        valueRef.current = next;
        form.setFieldValue(field.name, next);
        form.setFieldTouched(field.name, true);
    };

    const addBlock = () => {
        const guid = v4();

        setFreshGuid(guid);
        commit([{ guid, note: "", files: [] }, ...valueRef.current]);
    };

    const changeNote = (guid: string, note: string) => {
        commit(valueRef.current.map((block) => (block.guid === guid ? { ...block, note } : block)));
    };

    const addFile = (guid: string) => async (file: File) => {
        const uploaded = await employer.uploadFile(file);

        if (uploaded) {
            commit(
                valueRef.current.map((block) =>
                    block.guid === guid ? { ...block, files: [...block.files, uploaded] } : block
                )
            );
        }
    };

    const removeFile = (guid: string) => (file: EmployerFileBase) => {
        commit(
            valueRef.current.map((block) =>
                block.guid === guid
                    ? { ...block, files: block.files.filter((item) => item.fileId !== file.fileId) }
                    : block
            )
        );
    };

    const removeBlock = (block: EmployerDocumentBlockForm) => {
        setSelected(({ [block.guid]: removed, ...rest }) => rest);
        commit(valueRef.current.filter((item) => item.guid !== block.guid));
    };

    const toggleSelected = (guid: string) => {
        setSelected((current) => ({ ...current, [guid]: !current[guid] }));
    };

    const printDocuments = async () => {
        const guids = blocks.filter((block) => selected[block.guid]).map((block) => block.guid);

        setPrinting(true);
        await employer.printDocuments(guids);
        setPrinting(false);
    };

    const canPrint = blocks.length > 0;
    const selectedCount = blocks.filter((block) => selected[block.guid]).length;
    const savedGuids = employer.documentBlocks.map((block) => block.guid);

    return (
        <>
            {(canChange || canPrint) && (
                <>
                    <div className={styles.tools}>
                        {canChange && (
                            <PlanrButton
                                type="lightgreenish"
                                size="small"
                                icon="general-plus-big"
                                onClick={addBlock}
                                className={styles.addButton}
                            >
                                Добавить документ
                            </PlanrButton>
                        )}

                        {canPrint && (
                            <PlanrButton
                                type="secondary"
                                size="small"
                                icon="general-print"
                                onClick={printDocuments}
                                className={styles.printButton}
                                title={
                                    selectedCount > 0
                                        ? `${texts.print} отмеченных документов`
                                        : `${texts.print} всех документов`
                                }
                            >
                                {`${texts.print}: ${selectedCount > 0 ? selectedCount : "все"}`}
                            </PlanrButton>
                        )}
                    </div>
                </>
            )}

            <Card className={styles.card}>
                <LocalLoader active={printing} />

                {blocks.map((block) => (
                    <div
                        key={block.guid}
                        className={classnames(styles.block, {
                            [styles.selected]: !!selected[block.guid],
                            [styles.fresh]: !savedGuids.includes(block.guid),
                        })}
                    >
                        <div className={styles.blockHeader}>
                            <PlanrButton
                                type="neutral"
                                size="small"
                                round={true}
                                icon={selected[block.guid] ? "general-checkbox" : "general-zero-checkbox"}
                                title={selected[block.guid] ? "Не печатать этот документ" : "Печатать этот документ"}
                                onClick={() => toggleSelected(block.guid)}
                            />

                            <NoteInput
                                autoFocus={block.guid === freshGuid}
                                disabled={!canChange}
                                notes={notes}
                                value={block.note}
                                onChange={(note) => changeNote(block.guid, note)}
                            />

                            {canChange && (
                                <span className={styles.blockRemove}>
                                    <RemoveConfirmation<EmployerDocumentBlockForm>
                                        what={() => `документ${block.note ? ` «${block.note}»` : ""} со всеми файлами`}
                                        onConfirmed={removeBlock}
                                        render={({ confirmRemoving }) => (
                                            <GeneralIcon
                                                type="general-trash"
                                                title="Удалить документ"
                                                onClick={() => confirmRemoving(block)}
                                                style={{ color: "#E31818", cursor: "pointer" }}
                                            />
                                        )}
                                    />
                                </span>
                            )}
                        </div>

                        {canChange && (
                            <PlanRDndUploader
                                accept={ACCEPT}
                                multiple={true}
                                oneRow={true}
                                onFileSelected={addFile(block.guid)}
                            />
                        )}

                        <div className={styles.fileList}>
                            {block.files.map((file) => (
                                <FileLink
                                    key={file.fileId}
                                    baseUrl={baseUrl}
                                    file={file}
                                    readOnly={!canChange}
                                    onRemove={removeFile(block.guid)}
                                />
                            ))}
                        </div>
                    </div>
                ))}
            </Card>
        </>
    );
};

/**
 * Примечание к документу: обычное поле ввода, но уже встречавшиеся примечания
 * предлагаются списком ниже — одним кликом подставляются в поле.
 *
 * Список открываем и закрываем сами (выбор, Enter, Escape, потеря фокуса):
 * Blueprint принимал клик по самому полю за «клик снаружи» и список только мигал
 */
const NoteInput = ({ value, notes, disabled, autoFocus, onChange }: NoteInputProps) => {
    const [open, setOpen] = useState(false);
    // фильтруем только пока печатают: по кнопке списка показываем все виды документов
    const [filtering, setFiltering] = useState(false);
    const input = useRef<HTMLInputElement | null>(null);

    const query = value.trim().toLowerCase();
    const filtered = notes.filter((note) => note.toLowerCase().includes(query));
    const suggestions = filtering && query && filtered.length ? filtered : notes;

    const select = (note: string) => {
        setOpen(false);
        onChange(note);
    };

    const showAll = () => {
        setFiltering(false);
        setOpen(true);
    };

    return (
        <div className={styles.noteField}>
            <PlanrPopover
                {...MATCH_TARGET_WIDTH}
                fill={true}
                isOpen={open && !disabled && suggestions.length > 0}
                autoFocus={false}
                enforceFocus={false}
                onClose={doNothing}
                content={
                    <Menu className={styles.suggestions}>
                        {suggestions.map((note) => (
                            <MenuItem
                                key={note}
                                text={note}
                                shouldDismissPopover={false}
                                // mousedown вместо click: срабатывает раньше, чем поле потеряет фокус
                                onMouseDown={(e: React.MouseEvent) => {
                                    e.preventDefault();
                                    select(note);
                                }}
                            />
                        ))}
                    </Menu>
                }
            >
                <InputGroup
                    autoFocus={autoFocus}
                    className="planr-default-input"
                    disabled={disabled}
                    fill={true}
                    inputRef={(ref: HTMLInputElement | null) => {
                        input.current = ref;
                    }}
                    placeholder={NOTE_PLACEHOLDER}
                    small={true}
                    title={value || NOTE_HINT}
                    value={value}
                    onBlur={() => setOpen(false)}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setFiltering(true);
                        setOpen(true);
                        onChange(e.currentTarget.value);
                    }}
                    onFocus={showAll}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        // Enter завершает ввод примечания: закрывает список, но не сохраняет карточку
                        if (e.key === "Enter") {
                            e.preventDefault();
                            e.stopPropagation();
                            setOpen(false);
                        }

                        if (e.key === "Escape") {
                            setOpen(false);
                        }
                    }}
                    rightElement={
                        disabled || !notes.length ? undefined : (
                            <span
                                className={styles.noteToggle}
                                title="Уже загруженные документы"
                                // preventDefault оставляет курсор в поле, иначе оно не узнает
                                // о потере фокуса и список некому будет закрыть
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    input.current && input.current.focus();

                                    if (open && !filtering) {
                                        setOpen(false);
                                    } else {
                                        showAll();
                                    }
                                }}
                            >
                                <GeneralIcon type="general-chevron-down" size={14} />
                            </span>
                        )
                    }
                />
            </PlanrPopover>
        </div>
    );
};

const doNothing = () => undefined;

interface EmployerDocumentsProps {
    baseUrl: string;
    fieldProps: FieldProps;
    employer: EmployerType;
    canChange: boolean;
    /** примечания, уже встречающиеся в базе — подсказки для поля */
    notes: string[];
}

interface NoteInputProps {
    value: string;
    notes: string[];
    disabled: boolean;
    autoFocus: boolean;
    onChange: (note: string) => void;
}

export const EmployerDocuments = observer(Documents);
